import { Token } from '@emberswap/emberswap-sdk2/dist/entities'
import ChainId from '@emberswap/emberswap-sdk2/dist/enums/ChainId'
import { Disclosure, Transition } from '@headlessui/react'
import { FC,  useCallback, useMemo, useState } from 'react'

import { classNames } from '../../functions'
import { useIsMounted } from '../../hooks'
import { useTokens } from '../../hooks/Token/useTokens'
import { useCustomTokens, useSettings } from '../../state/user/hooks'
import { Currency } from '../Currency'
import { ChevronRightIcon, InformationCircleIcon } from '../Icons'
import { Tab } from '../Tabs'
import { TokenSelector } from '../TokenSelector'
import { Tooltip } from '../Tooltip'

interface CurrencyCombinationDisclosureProps {
  chainId: ChainId
}
export const CurrencyCombinationDisclosure: FC<CurrencyCombinationDisclosureProps> = ({
  chainId,
}) => {
  const [tokenOverlay, setTokenOverlayOpen] = useState(false)
  const tokenMap = useTokens(chainId)
  const [customTokensMap] = useCustomTokens(chainId)

  const _tokenMap: Record<string, Token> = useMemo(
    () => ({ ...tokenMap, ...customTokensMap }),
    [tokenMap, customTokensMap]
  )
  const _tokenMapValues = useMemo(() => {
    return Object.values(_tokenMap)
  }, [_tokenMap])
  const isMounted = useIsMounted()
  const [{ userPath, userPathType }, { removePath, addPath, updateUserPathType }] =
    useSettings(chainId)

  const onSelect = useCallback(
    (currency: Token) => {
      if (
        userPath[currency.chainId] &&
        Object.values(userPath[currency.chainId]).some((t) => t.address == currency.wrapped.address)
      ) {
        removePath &&
          removePath({
            chainId: currency.chainId,
            address: currency.wrapped.address,
          })
      } else {
        addPath && addPath(currency)
      }
    },
    [addPath, removePath, userPath]
  )

  const handleClose = useCallback(() => {
    setTokenOverlayOpen(false)
  }, [])

  return useMemo(() => {
    if (!isMounted) return <></>
    return (
      <>
        <Disclosure>
          {({ open }) => (
            <div className="border-b select-none border-slate-200/5">
              <Disclosure.Button
                as="div"
                className="relative flex items-center justify-between w-full gap-3 cursor-pointer group rounded-xl"
              >
                <div className="flex items-center justify-center w-5 h-5">
                  <svg
                    className="-ml-1.5 text-slate-500"
                    width="28"
                    height="28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 100 125"
                    x="2px"
                    y="2px"
                  >
                    <path
                      strokeWidth={2}
                      stroke="#97A3B7"
                      d="m 40.875,6.84375 c -1.79015,0 -3.250011,1.459904 -3.25,3.25 l 0,27.53125 -21.59375,0 5.1875,-5.15625 c 1.269627,-1.261979 1.26583,-3.296709 0,-4.5625 -0.632915,-0.632897 -1.482964,-0.96875 -2.3125,-0.96875 -0.829537,0 -1.64742,0.336773 -2.28125,0.96875 L 5.9375,38.5625 c -1.26766,1.263953 -1.2639608,3.326093 0,4.59375 L 16.625,53.875 c 1.26396,1.267656 3.327921,1.265793 4.59375,0 1.265829,-1.26579 1.269628,-3.331771 0,-4.59375 l -5.1875,-5.15625 21.59375,0 0,11.75 c 2.1e-5,3.580201 2.919691,6.5 6.5,6.5 l 11.75,0 0,27.5625 c 1e-5,1.790097 1.459851,3.21875 3.25,3.21875 1.790151,0 3.250011,-1.428653 3.25,-3.21875 l 0,-27.5625 21.59375,0 -5.1875,5.15625 c -1.269627,1.261979 -1.26583,3.296709 0,4.5625 0.632913,0.632896 1.482964,0.96875 2.3125,0.96875 0.829537,0 1.64742,-0.336773 2.28125,-0.96875 L 94.0625,61.4375 c 1.26766,-1.263954 1.263961,-3.326093 0,-4.59375 L 83.375,46.125 c -1.263959,-1.267655 -3.32792,-1.265793 -4.59375,0 -1.265828,1.265791 -1.269627,3.331771 0,4.59375 l 5.1875,5.15625 -21.59375,0 0,-11.75 c -2.1e-5,-3.580201 -2.91969,-6.5 -6.5,-6.5 l -11.75,0 0,-27.53125 c -1.1e-5,-1.790096 -1.459851,-3.25 -3.25,-3.25 z m 3.25,37.28125 11.75,0 0,11.75 -11.75,0 z"
                      fill="#97A3B7"
                    />
                  </svg>
                </div>
                <div className="grid items-center justify-between w-full grid-cols-10 gap-1 py-4 ">
                    <div className="flex items-center col-span-5 gap-1 text-sm font-medium text-left break-words ">
                    Currency Combinations
                      <Tooltip
                        button={<InformationCircleIcon width={14} height={14} />}
                        panel={
                          <div className="flex flex-col gap-2 w-80">
                            <p className="text-xs font-medium text-slate-300">
                              Currency combinations play a crucial role in facilitating trading
                              between different tokens. The currency combinations are intermediary
                              tokens used to establish liquidity and enable seamless trading between
                              two other tokens that may not have a direct trading pair.
                            </p>
                            <p className="text-xs font-medium text-slate-300">
                              Currency combinations empower traders to access a wider range of
                              assets while maintaining the decentralized and non-custodial nature of
                              the exchange.
                            </p>
                          </div>
                        }
                      />
                    </div>
                  <div className="flex justify-end col-span-5 gap-1">
                    {userPathType === 'auto' ? (
                      <p className="text-sm font-medium group-hover:text-slate-400 text-slate-600">
                        Auto
                      </p>
                    ) : userPath &&
                      _tokenMap &&
                      chainId &&
                      userPath[chainId] &&
                      Object.values(userPath[chainId]).length > 0 ? (
                      <div
                        className="flex w-full h-full overflow-hidden overflow-x-scroll text-sm font-medium rounded-md scrollableContainer whitespace-nowrap group-hover:text-slate-400 text-slate-600"
                        onWheel={(e) => e.currentTarget.scrollBy(e.deltaY / 4, 0)}
                      >
                        {_tokenMapValues.map((t) => {
                          if (
                            chainId &&
                            t &&
                            userPath[chainId] &&
                            Object.values(userPath[chainId]).some(
                              (z) => z.address === t.wrapped.address
                            )
                          ) {
                            return (
                              <div className="p-1 m-0 flex flex-row items-center flex-grow gap-0.5 max-w-max ">
                                <div className="w-8 h-8  p-1 m-0 flex items-center flex-grow gap-0.5 max-w-max ">
                                  <Currency.Icon currency={t} width={32} height={32} disableLink />
                                </div>
                              </div>
                            )
                          }
                          return
                        })}
                      </div>
                    ) : (
                      <p className="text-sm font-medium group-hover:text-slate-400 text-slate-600">
                        Custom
                      </p>
                    )}
                    <div
                      className={classNames(
                        open ? 'rotate-90' : 'rotate-0',
                        'transition-all w-5 h-5 -mr-1.5 flex items-center delay-300'
                      )}
                    >
                      <ChevronRightIcon
                        width={16}
                        height={16}
                        className="group-hover:text-slate-200 text-slate-300"
                      />
                    </div>
                  </div>
                </div>
              </Disclosure.Button>

              <Transition
                unmount={false}
                className="transition-[max-height] overflow-hidden mb-3"
                enter="duration-300 ease-in-out"
                enterFrom="transform max-h-0"
                enterTo="transform max-h-[380px]"
                leave="transition-[max-height] duration-250 ease-in-out"
                leaveFrom="transform max-h-[380px]"
                leaveTo="transform max-h-0"
              >
                <Disclosure.Panel>
                  <Tab.Group
                    selectedIndex={userPathType === 'auto' ? 0 : 1}
                    onChange={(index: number) =>
                      updateUserPathType(index === 0 ? 'auto' : 'custom')
                    }
                  >
                    <Tab.List className="grid-cols-8 md:grid-cols-none md:flex">
                      <Tab
                        className={classNames(
                          userPathType === 'auto' ? 'bg-slate-400/20' : '',
                          'col-span-3 md:!flex-1 !rounded-none !border-transparent hover:bg-slate-400/40'
                        )}
                      >
                        Auto
                      </Tab>
                      <Tab
                        onClick={() => userPathType !== 'auto' && setTokenOverlayOpen(true)}
                        className={classNames(
                          userPathType !== 'auto' ? 'bg-slate-400/20' : '',
                          'col-span-5 md:!flex-1 !rounded-none border-transparent hover:bg-slate-400/40'
                        )}
                      >
                        {userPathType !== 'auto' ? 'Change Path' : 'Custom'}
                      </Tab>
                    </Tab.List>
                    <Tab.Panel />
                  </Tab.Group>
                </Disclosure.Panel>
              </Transition>
            </div>
          )}
        </Disclosure>

        <TokenSelector
          title="Change Path"
          onSelect={onSelect}
          currency={undefined}
          open={tokenOverlay}
          chainId={chainId}
          onClose={handleClose}
          includeNative={false}
          midSelector={true}
        />
      </>
    )
  }, [
    _tokenMap,
    _tokenMapValues,
    chainId,
    handleClose,
    isMounted,
    onSelect,
    tokenOverlay,
    updateUserPathType,
    userPath,
    userPathType,
  ])
}
